/**
 * om-cart.js - advanced cart page.
 *
 * Everything runs over AJAX and re-renders the cart section through the
 * Section Rendering API, so quantity changes, removals, notes, discount codes
 * and upsell adds never reload the page.
 *
 * DISCOUNT CODES - the important caveat
 * Shopify has no supported AJAX endpoint for applying a discount code to the
 * cart. The reliable mechanism is the same one Shopify's own shareable
 * discount links use:
 *
 *     GET /discount/<CODE>
 *
 * which attaches the code to the session and therefore to the cart. We fetch
 * that, re-read /cart.js, and confirm the code actually landed before telling
 * the shopper it worked - an invalid code silently does nothing otherwise.
 *
 * Removal has no official endpoint either. We attempt /cart/update.js with an
 * empty discount, then verify. If the code is still attached we say so plainly
 * rather than pretending it was removed.
 */
(function () {
  'use strict';

  if (window.__omCart) return;
  window.__omCart = true;

  var SECTION = '[data-om-cart]';

  function root() { return document.querySelector(SECTION); }
  function sectionId() {
    var el = root();
    return el && el.getAttribute('data-section-id');
  }

  function money(paise) {
    var amount = (paise || 0) / 100;
    var dec = Math.round(amount * 100) % 100 !== 0;
    return 'Rs. ' + new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: dec ? 2 : 0,
      maximumFractionDigits: 2
    }).format(amount);
  }

  function busy(on) {
    var el = root();
    if (el) el.classList.toggle('om-cart--busy', on);
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  /** Re-render the cart section in place. */
  function refresh() {
    var id = sectionId();
    if (!id) { window.location.reload(); return Promise.resolve(); }

    return fetch('/cart?section_id=' + encodeURIComponent(id))
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector(SECTION);
        var live = root();
        if (!fresh || !live) { window.location.reload(); return; }

        // Preserve what the shopper was typing.
        var note = live.querySelector('[data-om-note]');
        var noteVal = note ? note.value : null;
        var focusKey = document.activeElement && document.activeElement.closest('[data-line-key]');
        focusKey = focusKey ? focusKey.getAttribute('data-line-key') : null;

        live.innerHTML = fresh.innerHTML;

        if (noteVal !== null) {
          var newNote = live.querySelector('[data-om-note]');
          if (newNote && !newNote.value) newNote.value = noteVal;
        }
        if (focusKey) {
          var back = live.querySelector('[data-line-key="' + focusKey + '"] [data-om-qty-input]');
          if (back) back.focus();
        }

        document.dispatchEvent(new CustomEvent('om:cart:rendered'));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      });
  }

  function lineError(key, message) {
    var row = document.querySelector('[data-line-key="' + key + '"]');
    if (!row) return;
    var el = row.querySelector('[data-om-line-error]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  // ------------------------------------------------------ quantity ------

  function changeLine(key, quantity) {
    busy(true);
    lineError(key, '');

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error(res.data && res.data.description);
        return refresh();
      })
      .catch(function (err) {
        lineError(key, (err && err.message) || 'Could not update that item.');
      })
      .then(function () { busy(false); });
  }

  var qtyTimer = {};

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-om-qty]');
    if (!btn) return;
    e.preventDefault();

    var row = btn.closest('[data-line-key]');
    var input = row && row.querySelector('[data-om-qty-input]');
    if (!input) return;

    var step = btn.getAttribute('data-om-qty') === 'up' ? 1 : -1;
    var next = Math.max(0, (parseInt(input.value, 10) || 0) + step);
    input.value = next;

    changeLine(row.getAttribute('data-line-key'), next);
  });

  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-om-qty-input]');
    if (!input) return;
    var row = input.closest('[data-line-key]');
    if (!row) return;

    var key = row.getAttribute('data-line-key');
    clearTimeout(qtyTimer[key]);
    qtyTimer[key] = setTimeout(function () {
      changeLine(key, Math.max(0, parseInt(input.value, 10) || 0));
    }, 300);
  });

  document.addEventListener('click', function (e) {
    var rm = e.target.closest('[data-om-remove]');
    if (!rm) return;
    e.preventDefault();
    var row = rm.closest('[data-line-key]');
    if (row) changeLine(row.getAttribute('data-line-key'), 0);
  });

  // ---------------------------------------------------------- note ------

  var noteTimer = null;

  document.addEventListener('input', function (e) {
    var note = e.target.closest('[data-om-note]');
    if (!note) return;

    var status = document.querySelector('[data-om-note-status]');
    clearTimeout(noteTimer);
    noteTimer = setTimeout(function () {
      if (status) status.textContent = 'Saving...';
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note.value })
      })
        .then(function () { if (status) status.textContent = 'Saved'; })
        .catch(function () { if (status) status.textContent = 'Could not save'; })
        .then(function () {
          setTimeout(function () { if (status) status.textContent = ''; }, 2500);
        });
    }, 600);
  });

  // ------------------------------------------------------ discount ------

  function discountMessage(text, kind) {
    var el = document.querySelector('[data-om-discount-msg]');
    if (!el) return;
    el.textContent = text || '';
    el.hidden = !text;
    el.className = 'om-cart__discount-msg' + (kind ? ' is-' + kind : '');
  }

  function codesOnCart(cart) {
    return (cart.discount_codes || [])
      .filter(function (d) { return d.applicable !== false; })
      .map(function (d) { return String(d.code).toUpperCase(); });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-om-discount-form]');
    if (!form) return;
    e.preventDefault();

    var input = form.querySelector('[data-om-discount-input]');
    var code = (input.value || '').trim();
    if (!code) return;

    busy(true);
    discountMessage('Checking code...', 'pending');

    // Shopify's own shareable-discount-link mechanism: attaches the code to
    // the session, which applies it to the cart and carries into checkout.
    fetch('/discount/' + encodeURIComponent(code), { redirect: 'follow' })
      .then(function () { return getCart(); })
      .then(function (cart) {
        var applied = codesOnCart(cart).indexOf(code.toUpperCase()) !== -1;
        if (!applied) {
          discountMessage('That code isn’t valid for the items in your cart.', 'error');
          busy(false);
          return;
        }
        input.value = '';
        return refresh().then(function () {
          discountMessage('Code applied.', 'success');
          busy(false);
        });
      })
      .catch(function () {
        discountMessage('Could not apply that code. Please try again.', 'error');
        busy(false);
      });
  });

  document.addEventListener('click', function (e) {
    var rm = e.target.closest('[data-om-discount-remove]');
    if (!rm) return;
    e.preventDefault();

    var code = rm.getAttribute('data-code') || '';
    busy(true);

    // No official removal endpoint. Try clearing, then verify honestly.
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: '' })
    })
      .catch(function () {})
      .then(getCart)
      .then(function (cart) {
        var still = codesOnCart(cart).indexOf(code.toUpperCase()) !== -1;
        return refresh().then(function () {
          if (still) {
            discountMessage('This code stays attached to your session - you can remove it at checkout.', 'error');
          } else {
            discountMessage('Code removed.', 'success');
          }
          busy(false);
        });
      })
      .catch(function () { busy(false); });
  });

  // -------------------------------------------------------- upsell ------

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-om-cart-upsell-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();

    var row = btn.closest('[data-om-cart-upsell-item]');
    var field = row && row.querySelector('[data-om-cart-upsell-variant]');
    if (!field || !field.value) return;

    var original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: field.value, quantity: 1 }] })
    })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (d && d.status) throw new Error('add failed');
        return refresh();
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = original;
      });
  });

  // Upsell variant dropdown - price from the option, no network call.
  document.addEventListener('change', function (e) {
    var sel = e.target.closest('select[data-om-cart-upsell-variant]');
    if (!sel) return;
    var row = sel.closest('[data-om-cart-upsell-item]');
    var opt = sel.options[sel.selectedIndex];
    if (!row || !opt) return;
    var price = row.querySelector('[data-om-cart-upsell-price]');
    if (price) price.textContent = money(parseInt(opt.getAttribute('data-price'), 10) || 0);
  });

  // ----------------------------------------------------- shipping bar ---

  function paintShipping() {
    var bar = document.querySelector('[data-om-ship]');
    if (!bar) return;
    var threshold = parseInt(bar.getAttribute('data-threshold'), 10) || 0;
    if (!threshold) return;

    getCart().then(function (cart) {
      // Measured on the pre-discount subtotal, which is what Shopify uses for
      // free-shipping conditions.
      var amount = typeof cart.items_subtotal_price === 'number'
        ? cart.items_subtotal_price
        : cart.total_price;

      var fill = bar.querySelector('[data-om-ship-fill]');
      var text = bar.querySelector('[data-om-ship-text]');
      var pct = Math.min((amount / threshold) * 100, 100);

      if (fill) fill.style.width = pct.toFixed(1) + '%';
      if (text) {
        text.innerHTML = amount >= threshold
          ? '<strong>Free shipping unlocked.</strong>'
          : 'Add <strong>' + money(threshold - amount) + '</strong> more for free shipping';
      }
      bar.classList.toggle('is-won', amount >= threshold);
    }).catch(function () {});
  }

  document.addEventListener('om:cart:rendered', paintShipping);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', paintShipping);
  } else {
    paintShipping();
  }
})();
